import { useOverlayPortalRoot } from "@/components/modal/overlay.hooks";
import { ChalkEmployeeGuard } from "@/components/admin/ChalkEmployeeGuard";
import { Icon } from "@/components/icons/Icon";
import { MenuContainerV2, MenuSectionV2 } from "@/components/interactive/MenuContainerV2";
import { MenuItemV2 } from "@/components/interactive/MenuItemV2";
import { SelectMenuV2 } from "@/components/interactive/SelectMenuV2";
import { SwitchV2 } from "@/components/interactive/SwitchV2";
import { ViewportAwareFixedMenuV2 } from "@/components/interactive/ViewportAwareFixedMenuV2";
import { ProjectNavbarAdminDiagnostics } from "@/components/navigation/ProjectNavbarAdminDiagnostics";
import {
  DEFAULT_CENSUS_MAX_STALENESS_SECONDS,
  getPersistedAdminOptionsWithDefaults,
  type PersistedAdminOptions,
  updatePersistedAdminOptions,
} from "@/utils/admin-options-manager";
import { ZIndex } from "@/constants/z-index";
import { useGlobalAppState } from "@/utils/app-state";
import { classNames } from "@/utils/classnames";
import {
  addDashboardFixtureCaptureEventListener,
  clearDashboardFixtureCapture,
  getDashboardFixtureCaptureFilename,
  getDashboardFixtureCaptureSummary,
  makeDashboardFixtureCaptureBundle,
  startDashboardFixtureCapture,
  stopDashboardFixtureCapture,
} from "@/utils/connect-query/fixtures/dashboard-fixture-capture";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { useIsImpersonating } from "@/utils/hooks/use-is-impersonating";
import {
  deleteAllImpersonationCookies,
  fixImpersonationCookies,
} from "@/utils/impersonation-utils";
import { downloadBlob } from "@/utils/link-utils";
import { setLineagePanelOpen } from "@/utils/lineage-panel/lineage-panel-store";
import { clearNetworkLog, setNetworkLogPanelOpen } from "@/utils/network-log/network-log-store";
import { useNetworkLogEntryCount } from "@/utils/network-log/use-network-log";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useRef, useState, type FC } from "react";
import { createPortal } from "react-dom";

const ADMIN_OPTIONS_MENU_GUTTER_PX = 4;
const ADMIN_OPTIONS_MENU_TOP_OFFSET_PX = -4;
const ADMIN_OPTIONS_MENU_WIDTH_PX = 300;
const ADMIN_OPTIONS_MENU_CLOSE_DELAY_MS = 100;

/** Only the boolean options render as switches. Keeping the switch list off `keyof
 *  PersistedAdminOptions` is what stops a future non-boolean option from being silently
 *  coerced into a checkbox. */
type BooleanAdminOptionKey = {
  [K in keyof PersistedAdminOptions]: PersistedAdminOptions[K] extends boolean ? K : never;
}[keyof PersistedAdminOptions];

interface AdminOptionDefinition {
  key: BooleanAdminOptionKey;
  label: string;
  reloadOnChange: boolean;
}

/** Widening this only helps a source that has already been censused successfully at least once:
 *  the metadata plane writes the cache row after a census completes, so a source that has never
 *  finished one has no stored snapshot to serve at any staleness.
 *
 *  "Any age" is a very large duration, not 0 -- absent or zero max_staleness means "force a live
 *  census" in the proto, which is the opposite of what this control is for. */
const CENSUS_MAX_STALENESS_OPTIONS = [
  {
    id: String(DEFAULT_CENSUS_MAX_STALENESS_SECONDS),
    value: String(DEFAULT_CENSUS_MAX_STALENESS_SECONDS),
    label: "24 hours (default)",
  },
  { id: String(7 * 24 * 60 * 60), value: String(7 * 24 * 60 * 60), label: "7 days" },
  { id: String(30 * 24 * 60 * 60), value: String(30 * 24 * 60 * 60), label: "30 days" },
  {
    id: String(100 * 365 * 24 * 60 * 60),
    value: String(100 * 365 * 24 * 60 * 60),
    label: "Any age",
  },
] as const;

const ADMIN_OPTIONS: readonly AdminOptionDefinition[] = [
  {
    key: "useJsonGrpcTransport",
    label: "Use JSON gRPC transport",
    reloadOnChange: true,
  },
  {
    key: "logGrpcResponses",
    label: "Log gRPC responses",
    reloadOnChange: true,
  },
  {
    key: "limitedPerformanceMode",
    label: "Limited Performance Mode",
    reloadOnChange: true,
  },
  {
    key: "disableNetworkLog",
    label: "Disable network log",
    reloadOnChange: true,
  },
];

interface AdminOptionsMenuPosition {
  left: number;
  top: number;
}

interface ProjectNavbarAdminModeControlProps {
  collapsed?: boolean;
  className?: string;
}

export const ProjectNavbarAdminModeControl: FC<ProjectNavbarAdminModeControlProps> = ({
  collapsed,
  className,
}) => {
  const overlayPortalRoot = useOverlayPortalRoot();
  const { state, updateState } = useGlobalAppState();
  const isImpersonating = useIsImpersonating({ cookieOnly: true });
  const { projectId: urlProjectId } = useRouteInfo();
  const grpcQueryClient = useQueryClient();
  const rowRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<AdminOptionsMenuPosition | null>(null);
  const [adminOptions, setAdminOptions] = useState<PersistedAdminOptions>(
    getPersistedAdminOptionsWithDefaults,
  );
  const [fixtureCaptureSummary, setFixtureCaptureSummary] = useState(
    getDashboardFixtureCaptureSummary,
  );
  const networkLogEntryCount = useNetworkLogEntryCount();

  const clearMenuCloseTimer = useCallback(() => {
    if (closeTimerRef.current == null) return;

    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const closeAdminOptionsMenu = useCallback(() => {
    clearMenuCloseTimer();
    setMenuPosition(null);
  }, [clearMenuCloseTimer]);

  const openAdminOptionsMenu = useCallback(() => {
    clearMenuCloseTimer();

    const rect = rowRef.current?.getBoundingClientRect();
    if (rect == null) return;

    setMenuPosition({
      left: rect.right + ADMIN_OPTIONS_MENU_GUTTER_PX,
      top: rect.top + ADMIN_OPTIONS_MENU_TOP_OFFSET_PX,
    });
  }, [clearMenuCloseTimer]);

  const scheduleAdminOptionsMenuClose = useCallback(() => {
    clearMenuCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setMenuPosition(null);
    }, ADMIN_OPTIONS_MENU_CLOSE_DELAY_MS);
  }, [clearMenuCloseTimer]);

  const toggleAdminOption = useCallback(
    (option: BooleanAdminOptionKey, reloadOnChange: boolean) => {
      const nextValue = !adminOptions[option];
      const nextOptions = {
        ...adminOptions,
        [option]: nextValue,
      };

      setAdminOptions(nextOptions);
      updatePersistedAdminOptions({ [option]: nextValue } as Partial<PersistedAdminOptions>);

      if (reloadOnChange) {
        window.location.reload();
      }
    },
    [adminOptions],
  );

  const handleCensusMaxStalenessChange = useCallback((value: string) => {
    // Reload for the same reason every boolean option above does: the census staleness is read
    // from localStorage at call time by the catalog surfaces, and TanStack Query keys the stored
    // census by the request it was fetched with. Reloading is what stops the tree, the
    // autocomplete cache, and the catalog graph from disagreeing about which key is current.
    updatePersistedAdminOptions({ censusMaxStalenessSeconds: Number(value) });
    window.location.reload();
  }, []);

  const handleClearImpersonationCookies = useCallback(() => {
    closeAdminOptionsMenu();
    deleteAllImpersonationCookies();
  }, [closeAdminOptionsMenu]);

  const handleGetImpersonationCookies = useCallback(async () => {
    closeAdminOptionsMenu();
    await fixImpersonationCookies(grpcQueryClient, urlProjectId);
  }, [closeAdminOptionsMenu, grpcQueryClient, urlProjectId]);

  const handleStartFixtureCapture = useCallback(() => {
    closeAdminOptionsMenu();
    startDashboardFixtureCapture({ reset: true });
    window.location.reload();
  }, [closeAdminOptionsMenu]);

  const handleStopFixtureCapture = useCallback(() => {
    closeAdminOptionsMenu();
    stopDashboardFixtureCapture();
    window.location.reload();
  }, [closeAdminOptionsMenu]);

  const handleDownloadFixtureCapture = useCallback(() => {
    closeAdminOptionsMenu();
    downloadBlob(
      `${JSON.stringify(makeDashboardFixtureCaptureBundle(), null, 2)}\n`,
      getDashboardFixtureCaptureFilename(),
      "application/json;charset=utf-8",
    );
  }, [closeAdminOptionsMenu]);

  const handleClearFixtureCapture = useCallback(() => {
    closeAdminOptionsMenu();
    clearDashboardFixtureCapture();
  }, [closeAdminOptionsMenu]);

  const handleOpenNetworkLog = useCallback(() => {
    closeAdminOptionsMenu();
    setNetworkLogPanelOpen(true);
  }, [closeAdminOptionsMenu]);

  const handleOpenLineagePanel = useCallback(() => {
    closeAdminOptionsMenu();
    setLineagePanelOpen(true);
  }, [closeAdminOptionsMenu]);

  const handleClearNetworkLog = useCallback(() => {
    closeAdminOptionsMenu();
    clearNetworkLog();
  }, [closeAdminOptionsMenu]);

  useEffect(() => {
    setAdminOptions(getPersistedAdminOptionsWithDefaults());
  }, []);

  useEffect(() => {
    setFixtureCaptureSummary(getDashboardFixtureCaptureSummary());
    return addDashboardFixtureCaptureEventListener(() => {
      setFixtureCaptureSummary(getDashboardFixtureCaptureSummary());
    });
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current == null) return;

      window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  if (collapsed) return null;

  return (
    <div className={classNames("flex shrink-0 flex-col px-cds-6 pt-cds-10", className)}>
      <ChalkEmployeeGuard hideTooltip ignoreAdminMode>
        <div
          ref={rowRef}
          onMouseEnter={openAdminOptionsMenu}
          onMouseLeave={scheduleAdminOptionsMenuClose}
          onFocus={openAdminOptionsMenu}
          onBlur={scheduleAdminOptionsMenuClose}
        >
          <ProjectNavbarAdminModeRow
            label={"Admin mode"}
            checked={state.adminMode}
            isImpersonating={isImpersonating}
            onChange={(checked) => updateState({ adminMode: checked })}
          />
        </div>
      </ChalkEmployeeGuard>
      {menuPosition != null &&
        typeof document !== "undefined" &&
        createPortal(
          <ViewportAwareFixedMenuV2
            left={menuPosition.left}
            top={menuPosition.top}
            width={ADMIN_OPTIONS_MENU_WIDTH_PX}
            // Above the hovered category sidebar, which holds this control.
            style={{ zIndex: ZIndex.TOOLTIP }}
            onMouseEnter={clearMenuCloseTimer}
            onMouseLeave={scheduleAdminOptionsMenuClose}
          >
            <div aria-hidden={true} className={"absolute right-full top-0 h-full w-cds-2"} />
            <MenuContainerV2
              className={
                "w-full max-h-[var(--viewport-aware-fixed-menu-max-height)] overflow-y-auto"
              }
            >
              <ProjectNavbarAdminDiagnostics adminMode={state.adminMode} />
              <MenuSectionV2>
                {ADMIN_OPTIONS.map((option) => (
                  <ProjectNavbarAdminOptionRow
                    key={option.key}
                    label={option.label}
                    checked={adminOptions[option.key]}
                    onChange={() => toggleAdminOption(option.key, option.reloadOnChange)}
                  />
                ))}
              </MenuSectionV2>
              <MenuSectionV2>
                <div className={"px-cds-8 py-cds-4"}>
                  <SelectMenuV2
                    label={"Catalog census staleness"}
                    size={"sm"}
                    menuWidth={"content"}
                    options={CENSUS_MAX_STALENESS_OPTIONS}
                    value={String(adminOptions.censusMaxStalenessSeconds)}
                    onChange={handleCensusMaxStalenessChange}
                    triggerAriaLabel={"Catalog census staleness"}
                    menuZIndex={ZIndex.TOOLTIP}
                  />
                </div>
              </MenuSectionV2>
              <MenuSectionV2>
                <MenuItemV2
                  size={"sm"}
                  label={
                    fixtureCaptureSummary.enabled
                      ? "Stop dashboard fixture capture"
                      : "Start dashboard fixture capture"
                  }
                  leftIcon={fixtureCaptureSummary.enabled ? "remove" : "run"}
                  onClick={
                    fixtureCaptureSummary.enabled
                      ? handleStopFixtureCapture
                      : handleStartFixtureCapture
                  }
                />
                <MenuItemV2
                  size={"sm"}
                  label={`Download fixture capture (${fixtureCaptureSummary.recordCount})`}
                  leftIcon={"save"}
                  onClick={handleDownloadFixtureCapture}
                  disabled={fixtureCaptureSummary.recordCount === 0}
                />
                <MenuItemV2
                  size={"sm"}
                  label={"Clear fixture capture"}
                  leftIcon={"delete"}
                  onClick={handleClearFixtureCapture}
                  disabled={
                    fixtureCaptureSummary.recordCount === 0 &&
                    fixtureCaptureSummary.skippedCount === 0
                  }
                />
              </MenuSectionV2>
              <MenuSectionV2>
                <MenuItemV2
                  size={"sm"}
                  label={`Open network log (${networkLogEntryCount})`}
                  leftIcon={"network"}
                  onClick={handleOpenNetworkLog}
                  disabled={adminOptions.disableNetworkLog}
                />
                <MenuItemV2
                  size={"sm"}
                  label={"Clear network log"}
                  leftIcon={"delete"}
                  onClick={handleClearNetworkLog}
                  disabled={networkLogEntryCount === 0}
                />
              </MenuSectionV2>
              <MenuSectionV2>
                <MenuItemV2
                  size={"sm"}
                  label={"Open lineage"}
                  leftIcon={"workflow"}
                  onClick={handleOpenLineagePanel}
                />
              </MenuSectionV2>
              <MenuSectionV2>
                <MenuItemV2
                  size={"sm"}
                  label={"Clear impersonation cookies"}
                  leftIcon={"delete"}
                  onClick={handleClearImpersonationCookies}
                />
                <MenuItemV2
                  size={"sm"}
                  label={"Get impersonation cookies"}
                  leftIcon={"personalization"}
                  onClick={handleGetImpersonationCookies}
                  disabled={!urlProjectId}
                />
              </MenuSectionV2>
            </MenuContainerV2>
          </ViewportAwareFixedMenuV2>,
          overlayPortalRoot ?? document.body,
        )}
    </div>
  );
};

interface ProjectNavbarAdminModeRowProps {
  label: string;
  checked: boolean;
  isImpersonating: boolean;
  onChange: (checked: boolean) => void;
}

const ProjectNavbarAdminModeRow: FC<ProjectNavbarAdminModeRowProps> = ({
  label,
  checked,
  isImpersonating,
  onChange,
}) => {
  const switchId = useId();
  const labelId = useId();

  return (
    <div
      className={
        "flex h-7 select-none items-center justify-between gap-cds-8 rounded-cds-6 px-cds-8 " +
        "text-cds-md font-medium text-zinc-700 transition-colors duration-75 " +
        "hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-white"
      }
    >
      <label
        id={labelId}
        htmlFor={switchId}
        className={"flex min-w-0 items-center gap-cds-4 cursor-pointer"}
      >
        <span className="truncate">{label}</span>
        {checked && (
          <Icon
            name="wrench"
            size="md"
            className="shrink-0"
            aria-hidden
            data-testid="admin-mode-wrench-icon"
          />
        )}
        {isImpersonating && (
          <Icon
            name="personalization"
            size="md"
            className="shrink-0"
            aria-hidden
            data-testid="admin-mode-impersonation-icon"
          />
        )}
      </label>
      <SwitchV2 id={switchId} checked={checked} onChange={onChange} aria-labelledby={labelId} />
    </div>
  );
};

interface ProjectNavbarAdminOptionRowProps {
  label: string;
  checked: boolean;
  onChange: () => void;
}

const ProjectNavbarAdminOptionRow: FC<ProjectNavbarAdminOptionRowProps> = ({
  label,
  checked,
  onChange,
}) => {
  const switchId = useId();
  const labelId = useId();

  return (
    <div
      className={
        "flex h-7 select-none items-center justify-between gap-cds-8 rounded-cds-3 px-cds-8 " +
        "text-cds-sm font-medium text-cds-ink transition-colors duration-75 " +
        "hover:bg-zinc-200/60 hover:text-zinc-950 dark:text-zinc-300 " +
        "dark:hover:bg-white/[0.06] dark:hover:text-white"
      }
    >
      <label id={labelId} htmlFor={switchId} className={"min-w-0 truncate cursor-pointer"}>
        {label}
      </label>
      <SwitchV2 id={switchId} checked={checked} onChange={onChange} aria-labelledby={labelId} />
    </div>
  );
};
